import { useState, useEffect } from 'react';
import { loadEnhancedTheme } from '../utils/storeModuleLoader.js';

export function useStores(themeId) {
  const [stores, setStores] = useState([]);
  const [config, setConfig] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!themeId) return;

    const loadStores = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Use the enhanced theme loader
        const enhancedTheme = await loadEnhancedTheme(themeId);
        
        // Extract stores and config from enhanced theme
        const rawStores = enhancedTheme.stores || [];
        const storeConfig = enhancedTheme.config || {};
        
        // Process and validate store data (simplified version)
        const validatedStores = Array.isArray(rawStores) ? rawStores.filter(store => 
          store && typeof store === 'object' && store.id && store.name
        ) : [];
        
        setStores(validatedStores);
        setConfig(storeConfig);
      } catch (err) {
        console.error(`Failed to load stores for theme ${themeId}:`, err);
        setError(err.message);
        setStores([]);
        setConfig({});
      } finally {
        setIsLoading(false);
      }
    };

    loadStores();
  }, [themeId]);

  // Filter stores by search query
  const filterStores = (searchQuery) => {
    if (!searchQuery || !stores.length) return stores;

    const query = searchQuery.toLowerCase();
    return stores.filter(store =>
      store.name.toLowerCase().includes(query) ||
      store.city.toLowerCase().includes(query) ||
      store.address.toLowerCase().includes(query) ||
      (store.features && store.features.some(feature => 
        feature.toLowerCase().includes(query)
      ))
    );
  };

  // Get stores by feature
  const getStoresByFeature = (feature) => {
    if (!feature || !stores.length) return stores;
    
    return stores.filter(store =>
      store.features && store.features.includes(feature)
    );
  };

  // Get nearest stores by coordinates
  const getNearestStores = (lat, lng, maxDistance = 50) => {
    if (!lat || !lng || !stores.length) return stores;

    const calculateDistance = (lat1, lng1, lat2, lng2) => {
      const R = 6371; // Earth's radius in km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLng = (lng2 - lng1) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLng/2) * Math.sin(dLng/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return R * c;
    };

    return stores
      .map(store => ({
        ...store,
        distance: calculateDistance(lat, lng, store.lat, store.lng)
      }))
      .filter(store => store.distance <= maxDistance)
      .sort((a, b) => a.distance - b.distance);
  };

  return {
    stores,
    config,
    isLoading,
    error,
    filterStores,
    getStoresByFeature,
    getNearestStores
  };
}

export default useStores;
